// src/modules/registers/LocationRegisterPage.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../../firebaseConfig';
import { collection, doc, setDoc, deleteDoc, getDoc, onSnapshot, getDocs, query, where } from 'firebase/firestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

// React Icons
import {
    FiPlus,
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiMapPin,
    FiTag,
    FiFolder,
    FiSearch
} from 'react-icons/fi';
import { MdLocationOn, MdCategory } from 'react-icons/md';

// Import global components
import {
    FormModal,
    ConfirmModal,
    SearchBar,
    Pagination,
    TableSkeleton,
    EmptyState,
    Toast,
    PageHeader,
    ComboBox,
    TagInput
} from '../../components/common';

import styles from './Logistics.module.css';

const ITEMS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 300;

// Categorias disponíveis para localizações
const LOCATION_CATEGORIES = [
    { id: 'mp_semi', name: 'MP e Semi Acabado', color: '#1a5276' },
    { id: 'embalagem_acabado', name: 'Embalagem e Produto Acabado', color: '#2e7d32' }
];

// Modernized Location Modal Component using FormModal
const LocationModalContent = ({
    editingItem,
    locationId,
    setLocationId,
    description,
    setDescription,
    category,
    setCategory,
    modalStatus,
    idAvailability,
    checkingId,
    loading,
    t
}) => {
    const isEditing = !!editingItem;
    const selectedCategory = LOCATION_CATEGORIES.find(c => c.id === category);
    const categoryBgColor = selectedCategory?.color || '#666';

    const getIdStatusIcon = () => {
        if (isEditing) return null;
        if (!locationId.trim()) return null;
        if (checkingId) return <FiRefreshCw size={16} className={styles.spinner} style={{ marginLeft: '8px' }} />;
        if (idAvailability === 'available') return <FiCheck size={16} style={{ color: '#28a745', marginLeft: '8px' }} />;
        if (idAvailability === 'taken') return <FiX size={16} style={{ color: '#dc3545', marginLeft: '8px' }} />;
        return null;
    };

    const isFormValid = () => {
        if (isEditing) return true;
        if (!locationId.trim()) return false;
        if (idAvailability === 'taken') return false;
        if (!category) return false;
        return true;
    };

    return (
        <>
            {/* Seção 1: Informações Básicas */}
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>
                        <MdLocationOn size={20} />
                    </div>
                    <h3 className={styles.sectionTitle}>{t('pages.locations.sectionInfoTitle', 'Informações da Localização')}</h3>
                    <span className={styles.sectionBadge}>{t('pages.locations.requiredBadge', 'Obrigatório')}</span>
                </div>
                <div className={styles.formGrid}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="locationId">
                            {t('pages.locations.idLabel', 'Identificação')} <span className={styles.required}>*</span>
                            {!isEditing && getIdStatusIcon()}
                        </label>
                        <input
                            id="locationId"
                            type="text"
                            value={locationId}
                            onChange={(e) => setLocationId(e.target.value.toUpperCase())}
                            className={`${styles.input} ${(!isEditing && idAvailability === 'taken') ? styles.inputError : ''}`}
                            required
                            disabled={isEditing || loading}
                            placeholder={t('pages.locations.idPlaceholder', 'Ex: DEP1-PRAT-01')}
                        />
                        {!isEditing && locationId && idAvailability === 'taken' && (
                            <small className={styles.errorTextSmall}>
                                {t('pages.locations.idTakenError', 'Esta identificação já está em uso. Por favor, escolha outra.')}
                            </small>
                        )}
                        {!isEditing && locationId && idAvailability === 'available' && (
                            <small className={styles.suggestionHint}>
                                <FiCheck size={12} style={{ marginRight: '4px' }} />
                                {t('pages.locations.idAvailable', 'Identificação disponível para cadastro.')}
                            </small>
                        )}
                        <small className={styles.fieldHint}>
                            {t('pages.locations.idHint', 'Código único que identificará esta localização no sistema')}
                        </small>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="description">
                            {t('pages.locations.descriptionLabel', 'Descrição')} <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="description"
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className={styles.input}
                            required
                            disabled={loading}
                            placeholder={t('pages.locations.descriptionPlaceholder', 'Ex: Depósito Principal - Prateleira 01')}
                        />
                        <small className={styles.fieldHint}>
                            {t('pages.locations.descriptionHint', 'Nome descritivo para fácil identificação')}
                        </small>
                    </div>
                </div>
            </div>

            {/* Seção 2: Categoria */}
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>
                        <MdCategory size={20} />
                    </div>
                    <h3 className={styles.sectionTitle}>{t('pages.locations.categorySectionTitle', 'Categoria')}</h3>
                </div>
                <div className={styles.formGrid}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="category">
                            {t('pages.locations.categoryLabel', 'Categoria')} <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.categorySelectWrapper}>
                            <select
                                id="category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className={`${styles.input} ${styles.select}`}
                                required
                                disabled={loading}
                                style={{
                                    borderLeft: `4px solid ${category ? categoryBgColor : '#ccc'}`
                                }}
                            >
                                <option value="">{t('pages.locations.categoryPlaceholder', 'Selecione uma categoria...')}</option>
                                {LOCATION_CATEGORIES.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {t(`pages.locations.categories.${cat.id}`, cat.name)}
                                    </option>
                                ))}
                            </select>
                            {category && (
                                <div className={styles.categoryPreview} style={{ marginTop: '8px' }}>
                                    <span className={styles.categoryBadge} style={{
                                        backgroundColor: categoryBgColor,
                                        color: '#ffffff',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '0.8rem',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <span style={{
                                            display: 'inline-block',
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '2px',
                                            backgroundColor: 'rgba(255,255,255,0.5)'
                                        }}></span>
                                        {selectedCategory ? t(`pages.locations.categories.${selectedCategory.id}`, selectedCategory.name) : ''}
                                    </span>
                                </div>
                            )}
                        </div>
                        <small className={styles.fieldHint}>
                            {t('pages.locations.categoryHint', 'Define o tipo de material que será armazenado nesta localização')}
                        </small>
                    </div>
                </div>
            </div>
        </>
    );
};

const LocationRegisterPage = () => {
    const can = usePagePermissions('localizacoes');
    const { t } = useTranslation();
    const [locations, setLocations] = useState([]);
    const [locationId, setLocationId] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [status, setStatus] = useState({ message: '', type: '' });
    const [modalStatus, setModalStatus] = useState({ message: '', type: '' });
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    const [idAvailability, setIdAvailability] = useState(null);
    const [checkingId, setCheckingId] = useState(false);

    // Delete confirm modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const locationsCollectionRef = collection(db, 'localizacoes');

    useEffect(() => {
        setInitialLoading(true);

        const unsubscribe = onSnapshot(
            locationsCollectionRef,
            (snapshot) => {
                const fetchedLocations = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setLocations(fetchedLocations);
                setInitialLoading(false);
            },
            (error) => {
                console.error("Erro no listener de localizações:", error);
                setStatus({ message: 'Erro na sincronização em tempo real', type: 'error' });
                setInitialLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    const checkIdAvailability = useCallback(async (id) => {
        if (!id || editingItem) {
            setIdAvailability(null);
            return;
        }

        setCheckingId(true);
        try {
            const locationDocRef = doc(db, 'localizacoes', id);
            const docSnap = await getDoc(locationDocRef);
            setIdAvailability(docSnap.exists() ? 'taken' : 'available');
        } catch (error) {
            console.error("Erro ao verificar ID:", error);
            setIdAvailability(null);
        } finally {
            setCheckingId(false);
        }
    }, [editingItem]);

    useEffect(() => {
        if (!locationId || editingItem) return;

        const timer = setTimeout(() => {
            checkIdAvailability(locationId);
        }, 300);

        return () => clearTimeout(timer);
    }, [locationId, checkIdAvailability, editingItem]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(handler);
    }, [searchTerm]);

    const handleCloseModal = () => {
        setLocationId('');
        setDescription('');
        setCategory('');
        setEditingItem(null);
        setIsModalOpen(false);
        setModalStatus({ message: '', type: '' });
        setIdAvailability(null);
    };

    const handleAddClick = () => {
        setEditingItem(null);
        setLocationId('');
        setDescription('');
        setCategory('');
        setIsModalOpen(true);
        setModalStatus({ message: '', type: '' });
        setIdAvailability(null);
    };

    const handleEditClick = (location) => {
        setLocationId(location.id);
        setDescription(location.description || '');
        setCategory(location.category || '');
        setEditingItem(location);
        setIsModalOpen(true);
        setModalStatus({ message: '', type: '' });
        setIdAvailability(null);
    };

    const handleSave = async (e) => {
        e?.preventDefault?.();
        setLoading(true);
        setModalStatus({ message: '', type: '' });

        const cleanId = locationId.trim().toUpperCase();
        const cleanDescription = description.trim();
        const cleanCategory = category;
        const isEditingMode = !!editingItem;

        if (!cleanId || !cleanDescription) {
            setModalStatus({ message: 'A identificação e a descrição são obrigatórias.', type: 'error' });
            setLoading(false);
            return;
        }

        if (!cleanCategory) {
            setModalStatus({ message: 'Selecione uma categoria para a localização.', type: 'error' });
            setLoading(false);
            return;
        }

        if (!isEditingMode && idAvailability === 'taken') {
            setModalStatus({ message: `A localização ${cleanId} já existe.`, type: 'error' });
            setLoading(false);
            return;
        }

        const categoryInfo = LOCATION_CATEGORIES.find(c => c.id === cleanCategory);

        const locationData = {
            identificacao: cleanId,
            description: cleanDescription,
            category: cleanCategory,
            categoryName: categoryInfo?.name || cleanCategory,
            categoryColor: categoryInfo?.color || '#666',
            updatedAt: new Date(),
            createdAt: isEditingMode ? (editingItem.createdAt || new Date()) : new Date(),
        };

        try {
            if (isEditingMode) {
                const locationDoc = doc(db, 'localizacoes', editingItem.id);
                await setDoc(locationDoc, locationData);
                setModalStatus({ message: `Localização ${cleanId} atualizada com sucesso!`, type: 'success' });
            } else {
                const locationDocRef = doc(db, 'localizacoes', cleanId);
                const docSnap = await getDoc(locationDocRef);

                if (docSnap.exists()) {
                    setModalStatus({ message: `Localização ${cleanId} já existe.`, type: 'error' });
                    setLoading(false);
                    return;
                }

                await setDoc(locationDocRef, locationData);
                setModalStatus({ message: `Localização ${cleanId} adicionada com sucesso!`, type: 'success' });
            }

            setTimeout(() => {
                handleCloseModal();
                setStatus({ message: isEditingMode ? `Localização ${cleanId} atualizada!` : `Localização ${cleanId} adicionada!`, type: 'success' });
                setTimeout(() => {
                    setStatus(prev => prev.type === 'success' ? { message: '', type: '' } : prev);
                }, 3000);
            }, 1000);

        } catch (error) {
            console.error("Erro ao salvar localização:", error);
            setModalStatus({ message: 'Erro ao salvar a localização.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (id) => {
        setDeleteTarget(id);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        const id = deleteTarget;
        if (!id) return;

        setIsDeleting(true);
        try {
            // Verifica se há estoque associado a esta localização
            const stockCollectionRef = collection(db, 'mp-current-stock');
            const stockSnapshot = await getDocs(stockCollectionRef);

            const hasStock = stockSnapshot.docs.some(doc => {
                const data = doc.data();
                const quantity = data.quantity || 0;
                return data.locationId === id && quantity > 0;
            });

            if (hasStock) {
                setStatus({
                    message: `❌ Não é possível excluir a localização "${id}". Existem produtos com estoque neste local. Remova ou transfira o estoque primeiro.`,
                    type: 'error'
                });
                setDeleteModalOpen(false);
                setIsDeleting(false);
                setTimeout(() => {
                    setStatus(prev => prev.type === 'error' ? { message: '', type: '' } : prev);
                }, 5000);
                return;
            }

            // Verifica movimentações
            const entriesRef = collection(db, 'mp-entries');
            const q = query(entriesRef, where("locationIds", "array-contains", id));
            const movementsSnap = await getDocs(q);

            if (!movementsSnap.empty) {
                // Usar ConfirmModal para perguntar sobre movimentações
                const confirmed = window.confirm(
                    `A localização "${id}" possui ${movementsSnap.size} movimentações registradas. Ao excluí-la, os históricos serão mantidos mas sem referência ao local. Deseja continuar?`
                );
                if (!confirmed) {
                    setDeleteModalOpen(false);
                    setIsDeleting(false);
                    return;
                }
            }

            const locationDoc = doc(db, 'localizacoes', id);
            await deleteDoc(locationDoc);
            setStatus({ message: `✅ Localização "${id}" excluída com sucesso!`, type: 'success' });
            setDeleteModalOpen(false);
            setIsDeleting(false);

            setTimeout(() => {
                setStatus(prev => prev.type === 'success' ? { message: '', type: '' } : prev);
            }, 3000);

        } catch (error) {
            console.error("Erro ao deletar localização:", error);
            setStatus({ message: 'Erro ao excluir a localização. Tente novamente.', type: 'error' });
            setDeleteModalOpen(false);
            setIsDeleting(false);
        }
    };

    const filteredLocations = useMemo(() => {
        if (!locations || locations.length === 0) return [];
        const lowerCaseSearchTerm = debouncedSearchTerm.toLowerCase();

        return locations.filter(item => {
            const matchesSearch = item.id?.toLowerCase().includes(lowerCaseSearchTerm) ||
                item.description?.toLowerCase().includes(lowerCaseSearchTerm);

            const matchesCategory = !categoryFilter || item.category === categoryFilter;

            return matchesSearch && matchesCategory;
        });
    }, [locations, debouncedSearchTerm, categoryFilter]);

    const totalFilteredItems = filteredLocations.length;
    const totalPages = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentLocations = useMemo(() => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredLocations.slice(startIndex, endIndex);
    }, [filteredLocations, page, ITEMS_PER_PAGE]);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    useEffect(() => {
        if (page > totalPages && totalPages > 0) {
            setPage(totalPages);
        } else if (page === 0 && totalPages > 0) {
            setPage(1);
        }
    }, [totalPages, page]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm, categoryFilter]);

    const getCategoryColor = (categoryId) => {
        const cat = LOCATION_CATEGORIES.find(c => c.id === categoryId);
        return cat?.color || '#666';
    };

    const getCategoryName = (categoryId) => {
        const cat = LOCATION_CATEGORIES.find(c => c.id === categoryId);
        return cat?.name || categoryId || 'Sem categoria';
    };

    // Handle search from SearchBar
    const handleSearch = (term) => {
        setSearchTerm(term);
        setPage(1);
    };

    return (
        <div className={styles.pageContainer}>
            {/* Location Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSave}
                isLoading={loading}
                error={modalStatus.type === 'error' ? modalStatus.message : ''}
                success={modalStatus.type === 'success' ? modalStatus.message : ''}
                title={editingItem ? t('pages.locations.editTitle', 'Editar Localização') : t('pages.locations.newTitle', 'Nova Localização')}
                subtitle={editingItem
                    ? t('pages.locations.editingLabel', 'Editando: {id}', { id: editingItem.id })
                    : t('pages.locations.newSubtitle', 'Preencha os dados abaixo para cadastrar uma nova localização')}
                submitText={editingItem ? t('common.save', 'Salvar Alterações') : t('pages.locations.register', 'Cadastrar Localização')}
                maxWidth="860px"
            >
                <LocationModalContent
                    editingItem={editingItem}
                    locationId={locationId}
                    setLocationId={setLocationId}
                    description={description}
                    setDescription={setDescription}
                    category={category}
                    setCategory={setCategory}
                    modalStatus={modalStatus}
                    idAvailability={idAvailability}
                    checkingId={checkingId}
                    loading={loading}
                    t={t}
                />
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setDeleteTarget(null);
                }}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
                title={t('pages.locations.deleteTitle', 'Excluir Localização')}
                message={t('pages.locations.deleteMessage', 'Tem certeza que deseja excluir a localização "{id}"? Esta ação não pode ser desfeita.', { id: deleteTarget || '' })}
                confirmText={t('common.delete', 'Excluir')}
                cancelText={t('common.cancel', 'Cancelar')}
                danger
            />

            {/* Page Header */}
            <PageHeader
                title={`${t('navigation.locations', 'Cadastro de Localizações')}${initialLoading ? ' ⏳' : ''}`}
                actionText={can.create ? t('pages.locations.newLocation', 'Nova Localização') : undefined}
                onAction={can.create ? handleAddClick : undefined}
                disabled={loading || initialLoading}
                showAction={!!can.create}
            />

            {/* Status Toast */}
            {status.message && !isModalOpen && (
                <Toast
                    message={status.message}
                    type={status.type}
                    onClose={() => setStatus({ message: '', type: '' })}
                />
            )}

            {/* Search and Filter */}
            <div className={styles.searchBar}>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <div className={styles.inputGroup} style={{ flex: 2 }}>
                        <label>{t('common.search', 'Buscar')}</label>
                        <SearchBar
                            placeholder={t('pages.locations.searchPlaceholder', 'ID ou Descrição...')}
                            onSearch={handleSearch}
                            disabled={loading || initialLoading}
                            debounceDelay={DEBOUNCE_DELAY}
                        />
                    </div>

                    <div className={styles.inputGroup} style={{ flex: 1 }}>
                        <label>{t('pages.locations.categoryLabel', 'Categoria')}</label>
                        <select
                            value={categoryFilter}
                            onChange={(e) => {
                                setCategoryFilter(e.target.value);
                                setPage(1);
                            }}
                            className={`${styles.input} ${styles.select}`}
                            disabled={loading || initialLoading}
                        >
                            <option value="">{t('pages.locations.allCategories', 'Todas as Categorias')}</option>
                            {LOCATION_CATEGORIES.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                    {t(`pages.locations.categories.${cat.id}`, cat.name)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableCard}>
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '15%' }}>{t('pages.locations.idLabel', 'Identificação (ID)')}</th>
                                <th style={{ width: '20%' }}>{t('pages.locations.categoryLabel', 'Categoria')}</th>
                                <th style={{ width: '45%' }}>{t('pages.locations.descriptionLabel', 'Descrição')}</th>
                                <th style={{ width: '20%' }} className={styles.actionHeader}>{t('common.actions', 'Ações')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {initialLoading ? (
                                <TableSkeleton columns={4} rows={5} />
                            ) : loading && locations.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className={styles.loadingCell}>
                                        <FiRefreshCw size={20} className={styles.spinner} /> {t('common.loading', 'Carregando...')}
                                    </td>
                                </tr>
                            ) : (
                                currentLocations.map((location) => (
                                    <tr key={location.id} className={styles.tableRow}>
                                        <td className={styles.idCell}>{location.id}</td>
                                        <td>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                backgroundColor: getCategoryColor(location.category),
                                                color: '#ffffff',
                                                fontSize: '0.75rem',
                                                fontWeight: '600'
                                            }}>
                                                {getCategoryName(location.category)}
                                            </span>
                                        </td>
                                        <td>{location.description || 'N/A'}</td>
                                        <td className={styles.actionCell}>
                                            {can.edit && (
                                                <button
                                                    className={`${styles.actionButton} ${styles.editButton}`}
                                                    onClick={() => handleEditClick(location)}
                                                    disabled={loading}
                                                    title={t('common.edit', 'Editar')}
                                                >
                                                    <FiEdit size={18} />
                                                </button>
                                            )}
                                            {can.delete && (
                                                <button
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    onClick={() => handleDeleteClick(location.id)}
                                                    disabled={loading}
                                                    title={t('common.delete', 'Excluir')}
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                            {(!initialLoading && !loading && currentLocations.length === 0) && (
                                <tr>
                                    <td colSpan={4}>
                                        <EmptyState
                                            title={debouncedSearchTerm || categoryFilter
                                                ? t('pages.locations.noLocationsFound', 'Nenhuma localização encontrada para os filtros aplicados')
                                                : t('pages.locations.noLocations', 'Nenhuma localização cadastrada')}
                                            message={debouncedSearchTerm || categoryFilter
                                                ? t('pages.locations.tryDifferentFilters', 'Tente ajustar os filtros de busca')
                                                : t('pages.locations.startAdding', 'Comece adicionando uma nova localização')}
                                            actionText={can.create ? t('pages.locations.newLocation', 'Nova Localização') : undefined}
                                            onAction={can.create ? handleAddClick : undefined}
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalFilteredItems}
                    onPageChange={handlePageChange}
                    disabled={initialLoading}
                    itemsLabel={t('pages.locations.locations', 'localizações')}
                />
            </div>
        </div>
    );
};

export default LocationRegisterPage;